/* eslint-disable max-statements */
import { ViewportState } from './types/viewport-state.type';

type RotationEntry = ViewportState['rotationHistory'][number];

export interface EquivalentRotationResult {
	angleRadians: number;
	angleDegrees: number;
	centerX: number;
	centerY: number;
	isPureTranslation: boolean;
}

const toRadians = (degrees: number): number => (degrees * Math.PI) / 180;
const toDegrees = (radians: number): number => (radians * 180) / Math.PI;

const normalizeRadians = (theta: number): number => {
	const twoPi = Math.PI * 2;
	let t = ((theta % twoPi) + twoPi) % twoPi;
	if (t > Math.PI) t -= twoPi;
	return t;
};

export const computeEquivalentRotationFromHistory = (history: RotationEntry[]): EquivalentRotationResult => {
	if (!history || history.length === 0) {
		return {
			angleRadians: 0,
			angleDegrees: 0,
			centerX: 0,
			centerY: 0,
			isPureTranslation: true
		};
	}

	const entries = [...history].sort((a, b) => a.timestamp - b.timestamp);

	let r00 = 1;
	let r01 = 0;
	let r10 = 0;
	let r11 = 1;
	let tx = 0;
	let ty = 0;

	for (let i = 1; i < entries.length; i += 1) {
		const prev = entries[i - 1];
		const curr = entries[i];
		const dTheta = normalizeRadians(toRadians(curr.rotation - prev.rotation));
		if (Math.abs(dTheta) < 1e-10) continue;

		const c = Math.cos(dTheta);
		const s = Math.sin(dTheta);
		const cx = curr.panX;
		const cy = curr.panY;

		const nr00 = c * r00 - s * r10;
		const nr01 = c * r01 - s * r11;
		const nr10 = s * r00 + c * r10;
		const nr11 = s * r01 + c * r11;

		const itx = c * tx - s * ty + (1 - c) * cx + s * cy;
		const ity = s * tx + c * ty - s * cx + (1 - c) * cy;

		r00 = nr00; r01 = nr01;
		r10 = nr10; r11 = nr11;
		tx = itx; ty = ity;
	}

	const angle = Math.atan2(r10, r00);
	const isPureTranslation = Math.abs(angle) < 1e-10;
	if (isPureTranslation) {
		return {
			angleRadians: 0,
			angleDegrees: 0,
			centerX: 0,
			centerY: 0,
			isPureTranslation: true
		};
	}

	const a = 1 - Math.cos(angle);
	const b = Math.sin(angle);
	const det = a * a + b * b;
	if (det < 1e-14) {
		return {
			angleRadians: angle,
			angleDegrees: toDegrees(angle),
			centerX: 0,
			centerY: 0,
			isPureTranslation: true
		};
	}

	const inv00 = a / det;
	const inv01 = -b / det;
	const inv10 = b / det;
	const inv11 = a / det;
	const cx = inv00 * tx + inv01 * ty;
	const cy = inv10 * tx + inv11 * ty;

	return {
		angleRadians: angle,
		angleDegrees: toDegrees(angle),
		centerX: cx,
		centerY: cy,
		isPureTranslation: false
	};
};
